import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import {
  Box,
  DialogActions,
  DialogContent,
  DialogContentText,
} from "@mui/material";
import type { FormikErrors } from "formik";
import { Formik, setNestedObjectValues } from "formik";
import { useTranslation } from "react-i18next";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

import type {
  ExtendedPatient,
  GetFormDraftResponse,
  GetPatientResponse,
  MhFormContext,
} from "@thalamos/aspire-common";
import {
  getBaseFormTemplate,
  isAmhpAccreditation,
  isGuestUser,
} from "@thalamos/aspire-common";

import { api, apiHooks } from "../../api.js";
import { LoggedInUserContext } from "../../Contexts.js";
import {
  isNotFoundStatus,
  isRecordId,
  throwUnansweredReadFailure,
} from "../../helpers/recordRead.js";
import { WithPatient } from "../../hoc/WithPatient.js";
import { usePatientTimeline } from "../../hooks/apiCalls.js";
import {
  autoSaveStateReducer,
  initalAutoSaveState,
} from "../../reducers/autoSaveReducer.js";
import { routeFns } from "../../routePaths.js";
import type { DefaultPageProps } from "../defaultProps.js";
import { BannerDialog } from "../FormProgressPage/helpers/BannerDialog.js";
import { EditPatientDialog } from "./EditPatientDialog.js";
import {
  DebouncedValidation,
  formDraftValidationProps,
} from "./formDraftValidation.js";
import {
  comparePatientDemographics,
  PatientDemographicsOutdatedBanner,
} from "./helpers/comparePatientDemographics.js";
import { safeCast } from "./helpers/safeCast.js";
import { ThalamosForm } from "./ThalamosForm.js";
import {
  Banner,
  BannerList,
  Button,
  FormTitle,
  renderInfoToast,
  renderWarningToast,
  Stepper,
} from "~/components/design-system/index.js";
import { PopupDialog } from "~/components/design-system/PopupDialog/PopupDialog.js";
import { TortusDictationProvider } from "~/components/design-system/TortusAvtModal/index.js";
import {
  FormFooterSection,
  PractitionerFormField,
} from "~/components/form/index.js";
import { PatientBanner } from "~/components/layout/index.js";
import { Container } from "~/components/layout/styleWrappers.js";

function PatientDataValidatorAndUpdater({
  patient,
  values,
  previousPartsFormData,
  setValues,
  patientEditParamSuccess,
}: {
  patient: ExtendedPatient;
  values: any;
  setValues: (values: any) => void;
  previousPartsFormData: any[];
  patientEditParamSuccess: boolean;
}) {
  const { t } = useTranslation();
  const [showAutoUpdateDialog, setShowAutoUpdateDialog] = useState(false);

  const isFirstFormPart = !previousPartsFormData.length;

  const { newPatientDemographics, patientDemographicsMatch } =
    comparePatientDemographics(patient, values);

  // If patient data is out of date on part 1 of a form, update it and show a dialog
  useEffect(() => {
    if (isFirstFormPart && !patientDemographicsMatch) {
      setValues({ ...values, patient: newPatientDemographics });
      setShowAutoUpdateDialog(true);
    }
  }, [
    values,
    setValues,
    isFirstFormPart,
    newPatientDemographics,
    patientDemographicsMatch,
  ]);

  return (
    <>
      {showAutoUpdateDialog && !patientEditParamSuccess && (
        <BannerDialog
          title={t("pages.formDraftCompletionPage.patientDetailsEdited")}
          bannerType={BannerList.INFO}
          message={t(
            "pages.formDraftCompletionPage.patientDetailsEditedBanner",
          )}
          onClose={() => setShowAutoUpdateDialog(false)}
        />
      )}
    </>
  );
}

export function FormDraftPageInner({
  formDraft,
  patient,
  refetchDraft,
}: {
  formDraft: GetFormDraftResponse<any>;
  patient: GetPatientResponse;
  refetchDraft: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [autoSaveState, dispatchAutoSaveState] = useReducer(
    autoSaveStateReducer,
    initalAutoSaveState,
  );

  const patientId = patient.id;

  const { patientTimeline, reloadPatientTimeline } = usePatientTimeline({
    patientId: patientId,
  });

  const nhsNumber = patient.nhsNumber;

  const [activeStep] = useState(0);

  const template = getBaseFormTemplate(formDraft)!;

  const isOutcomeForm = formDraft.formTemplate.id === "mha-136-monitoring";

  const userContext = useContext(LoggedInUserContext);
  const currentUser = userContext?.user;

  // Gate: redirect non-owners away from outcome form drafts.
  // The backend allows read access, but only the current owner should be
  // editing the form. Show a toast and send the user back to the incident.
  const isOutcomeFormOwner =
    !isOutcomeForm || currentUser?.id === formDraft.ownerUserId;

  useEffect(() => {
    if (!isOutcomeFormOwner) {
      renderWarningToast({
        message: t("pages.formDraftCompletionPage.outcomeFormNotOwner"),
      });
      navigate(routeFns.formContextPage(formDraft.formContextId, patient.id), {
        replace: true,
      });
    }
  }, [isOutcomeFormOwner, navigate, formDraft.formContextId, patient.id, t]);

  const [showCompleteConfirmDialog, setShowCompleteConfirmDialog] =
    useState(false);
  const [dialogSubmitAttempted, setDialogSubmitAttempted] = useState(false);
  const pendingSubmitRef = useRef<(() => void) | null>(null);

  // For outcome forms, pre-populate the practitioner (user) field from the
  // logged-in user so the completion dialog shows the current user's details
  // rather than the previous draft owner's.
  const initialValues = useMemo(() => {
    if (!isOutcomeForm || !currentUser) return formDraft.data;
    const isGuest = isGuestUser(currentUser);
    return {
      ...formDraft.data,
      user: {
        id: currentUser.id,
        email: currentUser.email,
        name: isGuest ? "" : currentUser.name,
        address: isGuest ? "" : currentUser.address.address,
        postalCode: isGuest ? "" : currentUser.address.postalCode,
        // Always start unconfirmed so the user must actively tick the
        // confirmation checkbox in the completion dialog before submitting.
        isConfirmed: false,
        isGuest,
        isSection12Approved: !isGuest
          ? currentUser.accreditations.some((a) => a.type === "section-12")
          : null,
        isApprovedClinician: !isGuest
          ? currentUser.accreditations.some(
              (a) => a.type === "approved-clinician",
            )
          : null,
        localAuthority: !isGuest
          ? (currentUser.accreditations.find(isAmhpAccreditation)
              ?.localAuthority ?? "")
          : "",
      },
    };
  }, [formDraft.data, isOutcomeForm, currentUser]);

  const redirectToFormPage = useCallback(() => {
    navigate(routeFns.formContextPage(formDraft.formContextId, patient.id));
  }, [navigate, formDraft.formContextId, patient.id]);

  const location = useLocation();

  const partTemplate = template.parts[formDraft.part - 1];
  if (partTemplate === undefined) {
    throw new Error(`Form template has no part ${String(formDraft.part)}`);
  }

  const params = new URLSearchParams(location.search);
  const patientEditParam = params.get("patientedit");
  const patientEditParamSuccess = params.get("patienteditsuccess");

  const requestedByUserName = formDraft.requestedByUserName || "";
  const requestedByTeamName = formDraft.requestedByTeamName
    ? `(${formDraft.requestedByTeamName})`
    : "";

  const createdByName = `${requestedByUserName}  ${requestedByTeamName}`;

  // While redirecting non-owners, render nothing to prevent a flash of content.
  if (!isOutcomeFormOwner) {
    return null;
  }

  return patient ? (
    <div>
      <PatientBanner
        patient={patient}
        patientTimeline={patientTimeline}
        nhsNumber={nhsNumber ?? undefined}
        reloadPatientTimeline={reloadPatientTimeline}
      />
      <Box sx={{ my: 4 }}>
        <Stepper
          steps={
            isOutcomeForm
              ? [
                  t("pages.formDraftFlow.standardFlow.stepOne"),
                  t("pages.formDraftFlow.standardFlow.stepThree"),
                ]
              : [
                  t("pages.formDraftFlow.standardFlow.stepOne"),
                  t("pages.formDraftFlow.standardFlow.stepTwo"),
                  t("pages.formDraftFlow.standardFlow.stepThree"),
                ]
          }
          activeStep={activeStep}
        />
      </Box>

      {formDraft.requestedReasons ? (
        <Box sx={{ mb: 2 }}>
          <Banner
            title={`Section 15 Amend Requested`}
            bannerType={BannerList.WARNING}
            body={[
              `Requested by ${createdByName}`,
              `Reason(s): ${formDraft.requestedReasons
                .map((r) => r.label)
                .join(", ")}.`,
              `Description: ${formDraft.requestedAdditionalInfo}`,
            ]}
          />
        </Box>
      ) : null}

      {formDraft.previousPartsFormData.length > 0 && (
        <PatientDemographicsOutdatedBanner
          patient={patient}
          currentValues={formDraft.previousPartsFormData[0].data}
        />
      )}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <FormTitle
          useReducedTopPadding={true}
          hasTitleBottomMargin={!!template.section}
          titleText={`${template.formName}${template.description && `:`} ${
            template.description
          }`}
          subtitleText={`${
            template.section ? `Section ${template.section}` : ""
          }`}
        />
      </Box>

      <Formik
        initialValues={initialValues}
        onSubmit={async (values: any) => {
          await api.drafts.updateData(
            formDraft.id,
            safeCast(partTemplate.dataSchema, values),
          );
          if (isOutcomeForm) {
            await api.outcomeForm.complete(formDraft.formId);
            navigate(
              routeFns.formContextPage(formDraft.formContextId, patient.id),
            );
          } else {
            navigate(routeFns.formDraftsSign(formDraft.id, patient.id));
          }
        }}
        validationSchema={partTemplate.dataSchema}
        {...formDraftValidationProps}
      >
        {({
          values,
          errors,
          touched,
          setValues,
          handleBlur,
          isSubmitting,
          setFieldTouched,
          submitForm,
          submitCount,
          validateForm,
          setTouched,
          /* and other goodies */
        }) => {
          const mhFormContext: MhFormContext = {
            previousPartsFormData: formDraft.previousPartsFormData,
            previousForms: formDraft.formContext.forms,
            linkedForms: formDraft.linkedForms,
            patientMhaState: patient.patientMhaState,
            patient,
          };

          /** Check whether there are validation errors on non-`user` fields. */
          const hasNonUserErrors = (errs: FormikErrors<any>): boolean => {
            const { user: _user, ...rest } = errs;
            return Object.keys(rest).length > 0;
          };

          /**
           * For outcome forms: validate the form, show errors on non-user fields,
           * and only open the practitioner-confirmation dialog when the rest of
           * the form is valid.
           */
          const handleOutcomeFormComplete = async () => {
            const errs = await validateForm();

            if (hasNonUserErrors(errs)) {
              // Touch every field so errors display on the form
              setTouched(setNestedObjectValues(errs, true));

              // Scroll to the first error after React re-renders
              setTimeout(() => {
                document
                  .querySelector(".thalamosErrorMessage :first-of-type")
                  ?.scrollIntoView({ behavior: "smooth", block: "center" });
              }, 0);
              return;
            }

            // Form body is valid — open the practitioner confirmation dialog.
            // Clear any previously-touched user fields so the dialog starts
            // without stale validation errors from an earlier submit attempt.
            const { user: _user, ...touchedWithoutUser } = touched;
            setTouched(touchedWithoutUser);
            pendingSubmitRef.current = submitForm;
            setDialogSubmitAttempted(false);
            setShowCompleteConfirmDialog(true);
          };

          // Build practitioner-field props that reference Formik's values.user
          const practitionerFieldProps = {
            validationSchema: partTemplate.dataSchema,
            context: mhFormContext,
            values,
            setValues,
            errors,
            touched:
              showCompleteConfirmDialog && dialogSubmitAttempted
                ? {
                    ...touched,
                    ...(setNestedObjectValues(errors, true) as typeof touched),
                  }
                : touched,
            handleBlur,
            setFieldTouched,
          };

          return (
            <>
              <TortusDictationProvider>
                <Box
                  data-testid={`form:${formDraft.formTemplate.id}`}
                  data-formid={formDraft.formId}
                  data-formcontextid={formDraft.formContextId}
                >
                  <Container>
                    <DebouncedValidation
                      values={values}
                      touched={touched}
                      submitCount={submitCount}
                      validateForm={validateForm}
                    />
                    <PatientDataValidatorAndUpdater
                      patient={patient}
                      values={values}
                      setValues={setValues}
                      previousPartsFormData={formDraft.previousPartsFormData}
                      patientEditParamSuccess={!!patientEditParamSuccess}
                    />
                    <ThalamosForm
                      partTemplate={partTemplate}
                      fieldProps={{
                        validationSchema: partTemplate.dataSchema,
                        context: mhFormContext,
                        values,
                        setValues,
                        errors,
                        // After a submit attempt, Formik marks only keys present
                        // in `values` as touched. If the draft data is sparse
                        // (missing schema fields), errors for those fields won't
                        // display. Fix: deep-merge error keys into touched so
                        // every field with a validation error is treated as
                        // touched, including nested paths (e.g. placeOfSafety.trustId).
                        // Error-touched keys are spread last so they always win.
                        touched:
                          submitCount > 0
                            ? {
                                ...touched,
                                ...setNestedObjectValues(errors, true),
                              }
                            : touched,
                        handleBlur,
                        setFieldTouched,
                      }}
                      submitCount={submitCount}
                      isSubmitting={isSubmitting}
                      isAutoSaving={autoSaveState.isLoading}
                      dispatchAutoSaveState={dispatchAutoSaveState}
                      formDraft={formDraft}
                      redirectToFormPage={redirectToFormPage}
                    />
                  </Container>
                </Box>
              </TortusDictationProvider>
              <FormFooterSection
                isSticky={true}
                disableSubmit={isSubmitting}
                saveLabel={
                  isOutcomeForm
                    ? t("buttonLabels.markComplete")
                    : t("buttonLabels.sign")
                }
                discardLabel={t("buttonLabels.goBack")}
                autoSaveState={autoSaveState}
                onSave={
                  isOutcomeForm
                    ? () => void handleOutcomeFormComplete()
                    : submitForm
                }
                onCancel={() => {
                  navigate(
                    routeFns.formContextPage(
                      formDraft.formContextId,
                      formDraft.patientId,
                    ),
                  );
                }}
              />

              {/* Outcome-form completion dialog with practitioner confirmation */}
              <PopupDialog
                open={showCompleteConfirmDialog}
                onClose={() => setShowCompleteConfirmDialog(false)}
                testId="outcome-form-complete-confirm-dialog"
              >
                <DialogContent>
                  <DialogContentText>
                    {t(
                      "pages.formDraftCompletionPage.outcomeFormCompleteConfirm",
                    )}
                  </DialogContentText>
                  <Box sx={{ mt: 2 }}>
                    <PractitionerFormField
                      field={{
                        type: "practitioner",
                        label: t(
                          "pages.formDraftCompletionPage.outcomeFormPractitionerHeading",
                        ),
                        field: "user",
                      }}
                      fieldProps={practitionerFieldProps}
                    />
                  </Box>
                </DialogContent>
                <DialogActions>
                  <Button
                    label={t("buttonLabels.cancel")}
                    variant="outlined"
                    onClick={() => setShowCompleteConfirmDialog(false)}
                  />
                  <Button
                    label={t("buttonLabels.markComplete")}
                    onClick={async () => {
                      const errs = await validateForm();
                      if (errs.user) {
                        // Show practitioner validation errors inside the dialog
                        setDialogSubmitAttempted(true);
                        setTouched(setNestedObjectValues(errs, true));
                        return;
                      }
                      setShowCompleteConfirmDialog(false);
                      pendingSubmitRef.current?.();
                    }}
                  />
                </DialogActions>
              </PopupDialog>
            </>
          );
        }}
      </Formik>
      {!!patientEditParam && <EditPatientDialog refetchDraft={refetchDraft} />}
    </div>
  ) : (
    <div></div>
  );
}

export function FormDraftCompletionPageWithPatient({
  patient,
}: {
  patient: GetPatientResponse;
}) {
  let { formDraftId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [
    { data: formDraft, loading: formDraftLoading, response, error },
    refetchDraft,
  ] = apiHooks.drafts.get(formDraftId!);

  useEffect(() => {
    if (!formDraftLoading) {
      if (isNotFoundStatus(response?.status)) {
        renderInfoToast({
          message: t("pages.formDraftFlow.draftUnavailable"),
        });
        navigate(routeFns.patientHome(patient.id), { replace: true });
      }
    }
  }, [formDraft, formDraftLoading, response?.status, navigate, patient.id, t]);

  throwUnansweredReadFailure({ error, response }, "draft");
  if (!formDraft?.id) {
    return <></>;
  }

  return (
    <FormDraftPageInner
      formDraft={formDraft}
      patient={patient}
      refetchDraft={refetchDraft}
    />
  );
}

export function FormDraftCompletionPage({}: DefaultPageProps) {
  const { patientId, formDraftId } = useParams();
  if (!isRecordId(formDraftId) || !isRecordId(patientId)) {
    return <Navigate to={routeFns.notFound()} replace />;
  }
  return (
    <WithPatient
      patientId={patientId}
      children={(patient) => (
        <FormDraftCompletionPageWithPatient patient={patient} />
      )}
    />
  );
}
